import { useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext.jsx'
import { useToast } from '../context/ToastContext.jsx'
import ConfigNotice from '../components/ConfigNotice.jsx'
import Seo from '../components/Seo.jsx'

// Minimum age to create an account. Change to 18 if you prefer.
const MIN_AGE = 21

export default function Register() {
  const { register, isConfigured } = useAuth()
  const toast = useToast()
  const navigate = useNavigate()
  const location = useLocation()
  // A calc handed over from the result-moment signup prompt, so we can drop the
  // user back on the calculator with it ready to save.
  const pendingCalc = location.state?.calc

  const [form, setForm] = useState({ name: '', email: '', password: '', confirm: '' })
  const [agree, setAgree] = useState({ research: false, age: false, marketing: false })
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [busy, setBusy] = useState(false)

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }))
  const toggle = (k) => (e) => setAgree((a) => ({ ...a, [k]: e.target.checked }))

  const submit = async (e) => {
    e.preventDefault()
    setError('')
    setNotice('')
    if (form.password.length < 6) return setError('Password must be at least 6 characters.')
    if (form.password !== form.confirm) return setError('Passwords do not match.')
    if (!agree.research) return setError('Please confirm you understand this is for research/educational use only.')
    if (!agree.age) return setError(`Please confirm you are ${MIN_AGE} or older.`)
    setBusy(true)
    const res = await register({ ...form, marketingOptIn: agree.marketing })
    setBusy(false)
    if (res.error) return setError(res.error)
    if (res.needsConfirmation) {
      return setNotice('Almost there — check your email to confirm your account, then log in.')
    }
    toast(`Account created — welcome, ${res.user.name}!`, 'green')
    navigate('/calculator', { replace: true, state: pendingCalc ? { calc: pendingCalc } : undefined })
  }

  return (
    <main className="page">
      <Seo title="Create your account | CalculateMyPeptides" noindex />
      <div className="container auth-wrap">
        <div className="card auth-card fade-in">
          <div className="auth-head">
            <h1>Create your account</h1>
            <p>Save calculations and build a dosing schedule.</p>
          </div>
          {!isConfigured && <ConfigNotice />}
          {notice && <div className="ok-notice">{notice}</div>}
          <form onSubmit={submit}>
            <div className="field">
              <label className="label">Name <span className="hint">(optional)</span></label>
              <input className="input" type="text" autoComplete="name"
                value={form.name} onChange={set('name')} placeholder="Alex" />
            </div>
            <div className="field">
              <label className="label">Email</label>
              <input className="input" type="email" autoComplete="email" required
                value={form.email} onChange={set('email')} placeholder="you@example.com" />
            </div>
            <div className="grid-2">
              <div className="field">
                <label className="label">Password</label>
                <input className="input" type="password" autoComplete="new-password" required
                  value={form.password} onChange={set('password')} placeholder="6+ characters" />
              </div>
              <div className="field">
                <label className="label">Confirm</label>
                <input className="input" type="password" autoComplete="new-password" required
                  value={form.confirm} onChange={set('confirm')} placeholder="Repeat" />
              </div>
            </div>
            <div className="consent">
              <label className="consent-row">
                <input type="checkbox" checked={agree.research} onChange={toggle('research')} />
                <span>
                  I understand this tool and its content are for <strong>informational and research
                  purposes only</strong> — not medical advice. <span className="req">*</span>
                </span>
              </label>
              <label className="consent-row">
                <input type="checkbox" checked={agree.age} onChange={toggle('age')} />
                <span>I confirm I am <strong>{MIN_AGE} years of age or older</strong>. <span className="req">*</span></span>
              </label>
              <label className="consent-row">
                <input type="checkbox" checked={agree.marketing} onChange={toggle('marketing')} />
                <span>
                  I’d like occasional offers from our sponsors (max twice per month). <span className="opt">Optional</span>
                </span>
              </label>
            </div>

            {error && <div className="form-error" style={{ marginBottom: 12 }}>{error}</div>}
            <button className="btn btn-primary btn-block" type="submit" disabled={busy}>
              {busy ? 'Creating account…' : 'Create account'}
            </button>
            <p className="muted small" style={{ marginTop: 12, marginBottom: 0, textAlign: 'center' }}>
              By creating an account you agree to our terms. <span className="req">*</span> Required.
            </p>
          </form>
          <div className="auth-foot">
            Already have an account? <Link to="/login">Log in</Link>
          </div>
        </div>
      </div>
    </main>
  )
}
